/**
 * @unscoped — Frontend trace_id helper — Cycle 25.
 *
 * 두 가지 식별자 분리:
 *   - sessionId : sessionStorage 에 영구 (탭 닫기 전까지). 사용자 한 명의 brower 세션 묶음 추적.
 *   - traceId   : 매 outgoing 요청 마다 새로 (32-hex). 단일 request lifecycle 추적.
 *
 * 둘 다 W3C/OTel 형식 (32-hex) — backend (cycle 23/24) 와 wire 호환.
 *
 * 사용:
 *   axios interceptor (api/client.ts) 가 매 요청 전에:
 *     headers['X-Session-Id'] = getSessionId();
 *     headers['X-Trace-Id']   = newTraceId();
 */

const SESSION_KEY = 'workchat:session-id';
const HEX_REGEX = /^[0-9a-f]{32}$/;

/**
 * crypto.getRandomValues 기반 32-hex 생성. crypto 미지원 시 Math.random fallback (낮은 우선).
 */
export function newTraceId(): string {
  if (typeof crypto !== 'undefined' && crypto.getRandomValues) {
    const bytes = new Uint8Array(16);
    crypto.getRandomValues(bytes);
    return Array.from(bytes, (b) => b.toString(16).padStart(2, '0')).join('');
  }
  // fallback (SSR / 매우 오래된 브라우저). 운영 환경 도달 가능성 낮음.
  let s = '';
  for (let i = 0; i < 16; i++) s += Math.floor(Math.random() * 256).toString(16).padStart(2, '0');
  return s;
}

/**
 * 한 탭의 session-id 를 sessionStorage 에서 reuse. 없으면 새로 만들고 저장.
 * 32-hex 위반된 stale 값 → 새로 생성 (정합성 보장).
 */
export function getSessionId(): string {
  if (typeof sessionStorage === 'undefined') return newTraceId();
  try {
    const stored = sessionStorage.getItem(SESSION_KEY);
    if (stored && HEX_REGEX.test(stored)) return stored;
    const fresh = newTraceId();
    sessionStorage.setItem(SESSION_KEY, fresh);
    return fresh;
  } catch {
    // private mode 등 → 단순 in-memory (page reload 시 새로) — 정상 동작 우선
    return newTraceId();
  }
}

/**
 * test 용 reset — sessionStorage 클리어.
 */
export function _resetSessionIdForTests(): void {
  if (typeof sessionStorage !== 'undefined') {
    try { sessionStorage.removeItem(SESSION_KEY); } catch { /* ignore */ }
  }
}
